import * as React from "react";
import {useDispatch, useSelector} from "react-redux";
import {PropsWithChildren, useEffect} from "react";
import {checkIfNotEmpty} from "../../helpers/validatorHelper";
import firebase from "firebase";
import {Typography} from "@material-ui/core";
import {LocalizerHelper} from "../../helpers/localizerHelper";
import BottomSheetOrderRecap from "../bottom_order_recap/BottomOrderRecap";
import {useNavigate, useParams} from "react-router";
import {evaluateDitsMenuItemPriceV2} from "../../helpers/ditsMenuItemHelper";
import {IAvailability, TimeSlot, UpdateTimeSlot} from "../../models";
import {LoaderGif} from "../loader";
import RestaurantHeader from "../restaurant/RestaurantHeader";
import {DEFAULT_ALLOWED_PAYMENT_METHODS, DELIVERY_MODES, PAYMENT_TYPES, RESPONSE_CODES} from "../../constants/data";
import ErrorDialog from "../dialogs/ErrorDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import Screen from '../screen';
import { confirmSessionOrder, requestBill, resetSessionOrder } from "../../helpers/apiHelper";
import './index.scss';
import DocumentSnapshot = firebase.firestore.DocumentSnapshot;
import { RootState } from "../../store";
import { DeliveryDetailsView } from "./DeliveryDetails";
import { PaymentMethodButtons } from "./PaymentMethodButtons";
import { DeliveryCostsRow } from "./DeliveryCostsRow";
import { updatePaymentMethod, updateTimeSlot } from "../../features/session/session.service";
import { SessionMenuItems } from "./SessionMenuItems";
import { FirestoreHelper } from "../../helpers/firestoreHelper";
import { TimeSlots } from "./TimeSlots";
import { formatPrice } from "../../helpers/formatHelper";
import { convertTimeSlotTimestamp } from "../../helpers/timeSlotHelper";
import { auth } from "../../firebase";
import { Session } from "../../models/session";

interface IConfirmOrderProps extends PropsWithChildren<any> {
    isRecap: boolean,
}

const OrderRecap = ({
    isRecap = false,
}: IConfirmOrderProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { restaurant: restaurantId, menu: menuId, link_alias: link_alias, dits: sessionId } = useParams<{ restaurant: string; menu: string, link_alias: string, dits: string}>();
    const firestoreUser = useSelector((state: RootState) => state.auth.firestoreUser);
    const restaurant = useSelector((state: RootState) => state.restaurant.restaurant);
    const session = useSelector((state: RootState) => state.session.session);
    const sessionMenuItems = useSelector((state: RootState) => state.session.menuItems);
    const bills = useSelector((state: RootState) => state.bill.bills);
    const selectedSlot = useSelector((state: RootState) => state.session.session?.time_slot);
    const paymentMethod = useSelector((state: RootState) => state.session.session?.payment_method);
    const hasTimeSlotSelection = useSelector((state: RootState) => state.session.session?.service_type?.has_time_slots ?? true);
    const availabilities = useSelector((state: RootState) => state.availability.slots);
    const mustPayFirst = useSelector((state: RootState) => state.session.session?.must_pay_first);

    const lastLinkAlias = (window.localStorage) ? (window.localStorage.getItem('chuzeat_last_alias_link') ?? '' ) : '';

    useEffect(() => {
        if (!!session) {
            const hasTimeSlot = !!selectedSlot;
            if (hasTimeSlotSelection && availabilities.length > 0 && !_validTimeSlot(session.time_slot)) {
                _selectDefaultTimeSlot();
            }
            if(!paymentMethod) {
                _selectDefaultPaymentMethod(session);
            }
        }
    }, [session]);

    const _validTimeSlot = (time_slot: TimeSlot | null): boolean => {
        if(!time_slot) return false;
        const now = firebase.firestore.Timestamp.now().toMillis();
        if(now > time_slot.end) return false;
        return true;
    }

    const _selectDefaultTimeSlot = () => { 
        console.log("_selectDefaultTimeSlot");
        const ditsMenuItemsWeight = sessionMenuItems.map((item) => (item?.prepare_weight ?? 1)).reduce((acc: number, item: number) => acc += item, 0);
        const deliveryMode = session?.delivery_mode;
        const now = firebase.firestore.Timestamp.now().toMillis();
        for (const availability of availabilities) {
            const timeValid = now <= availability.timeStart && now <= availability.timeEnd;
            const toShowPercentage = deliveryMode === DELIVERY_MODES.inPlace ? availability.takeAwayPercentage : (availability.takeAwayPercentage > availability.deliveryPercentage ? availability.takeAwayPercentage : availability.deliveryPercentage);
            const notAvailable = ditsMenuItemsWeight > availability.remainingTakeAwaySlots || toShowPercentage >= 100;
            if (!notAvailable && timeValid) {
                _updateSessionTimeSlot(convertTimeSlotTimestamp(availability));
                break;
            }
        }
    }

    const _selectDefaultPaymentMethod = (session: Session) => {
        console.log("_selectDefaultPaymentMethod");
        const paymentMethods = session.allowed_payment_methods;
        _updateSessionPaymentMethod(paymentMethods[0] ?? DEFAULT_ALLOWED_PAYMENT_METHODS);
    }

    const _updateSessionTimeSlot = async (availability: IAvailability) => {
        if(!restaurantId || !sessionId) return;
        const slot: UpdateTimeSlot = {
            start: availability.timeStart,
            end: availability.timeEnd,
            offset:availability.offset
        }
        await updateTimeSlot(restaurantId, sessionId, slot);
    }

    const _updateSessionPaymentMethod = async (paymentMethod: string) => {
        if(!restaurantId || !sessionId) return;
        await updatePaymentMethod(restaurantId, sessionId, paymentMethod);
    }

    // ERRORS AND LOADING
    const [isInError, setIsInError] = React.useState(false);
    const [error, setError] = React.useState(null);

    const [loading, setLoading] = React.useState(false);
    const [waitForBill, setWaitForBill] = React.useState(false);

    const [isRejected, setIsRejected] = React.useState(false);
    const [askConfirm, setAskConfirm] = React.useState(false);

    const [orderNeedApproval, setOrderNeedApproval] = React.useState(false);

    // DATA
    const [orderApprovementRequest, setOrderApprovementRequest] = React.useState<DocumentSnapshot|null>(null);

    let _minimumOrderAmount = 0;
    let _isMajorThanMinimumPrice = true;

    useEffect(() => {
        if (!!bills) {
            if (orderNeedApproval) {
                const alias = link_alias ?? lastLinkAlias;
                const url = `/menu/${alias}/waiting-order-approval/${restaurantId}/${sessionId}/${menuId}/${orderApprovementRequest?.id}`;
                navigate(url);
            } else {
                if (waitForBill) {
                    setLoading(false);
                    setWaitForBill(false);
                    if (!!mustPayFirst) {
                        const alias = link_alias ?? lastLinkAlias;
                        const url = `/menu/${alias}/payment/${restaurantId}/${sessionId}/${menuId}`;
                        navigate(url);
                    } else {
                        const alias = link_alias ?? lastLinkAlias;
                        const url = `/menu/${alias}/thanks/${restaurantId}/${sessionId}/${menuId}`;
                        navigate(url);
                    }
                }
            }
        }
    }, [bills]);

    const _closeErrorDialog = () => {
        setIsInError(false);
        setError(null);
    }

    const _closeErrorAndGoHomepage = () => {
        setIsRejected(false);
        const alias = link_alias ?? lastLinkAlias;
        navigate(`/menu/${alias}`, {replace: true});
    }

    const _showErrorMessage = (error: any) => {
        setLoading(false);
        setIsInError(true);
        setError(error);
    }

    const isUserComplete = () => (
        !!auth.currentUser && 
        !auth.currentUser.isAnonymous && 
        !!firestoreUser && 
        checkIfNotEmpty(firestoreUser?.email) && 
        checkIfNotEmpty(firestoreUser?.nickname)
    );
    
    const _proceedWithOrderFromRecap = () => {
        const isDelivery = session?.delivery_mode === DELIVERY_MODES.delivery;
        const requestUserData = session?.service_type?.request_user_data ?? false;
        const alias = link_alias ?? lastLinkAlias;
        const isUserCompleteFlag = isUserComplete();
        console.log(`isUserCompleteFlag [${isUserCompleteFlag}]`);
        if (!isUserCompleteFlag) {
            const url = `/menu/${alias}/signin/${restaurantId}/${sessionId}/${menuId}`;
            navigate(url);
        } else if (isDelivery) {
            if(requestUserData){
                const url = `/menu/${alias}/info-details/${restaurantId}/${sessionId}/${menuId}/delivery`;
                navigate(url);
            }else{
                _proceedWithConfirmOrder(false);
            }
        } else {
            if(requestUserData){
                const alias = link_alias ?? lastLinkAlias;
                const url = `/menu/${alias}/info-details/${restaurantId}/${sessionId}/${menuId}/takeaway`;
                navigate(url);
            }else{
                _proceedWithConfirmOrder(false);
            }
        }
    };

    const _confirmDialog = () =>{
        setAskConfirm(false);
        _proceedWithConfirmOrder(true);
    }
    
    const _closeConfirmDialog = () =>{
        setAskConfirm(false);
        setLoading(false);
    }

    const _proceedWithConfirmOrder = async(needReject:Boolean) => {
        console.log("_proceedWithConfirmOrder");
        setLoading(true);
        try{
            if(needReject){
                try{
                    if(restaurantId && menuId && sessionId){
                        const res = await resetSessionOrder(restaurantId, menuId, sessionId, paymentMethod ?? "cash");
                    }
                }catch(e){
                    setIsRejected(true);
                    setLoading(false);
                    return;
                }   
            }
            if(paymentMethod && restaurantId && menuId && sessionId){
                const response = await confirmSessionOrder(restaurantId, menuId, sessionId, paymentMethod);
                console.log("confirmSessionOrder response: ", response);
                setOrderNeedApproval(response.orderNeedApproval ?? false);
                const orderNeedApproval = response.orderNeedApproval ?? false;
                const orderApprovementRequestPath = response.orderApprovementRequest ?? null;
                console.log("orderApprovementRequestPath: ", orderApprovementRequestPath);
                if (!!restaurantId && !!sessionId) {
                    if (orderNeedApproval) {
                        if (checkIfNotEmpty(orderApprovementRequestPath)) {
                            const req = await FirestoreHelper.getSnapshot(orderApprovementRequestPath);
                            setOrderApprovementRequest(req);
                            const alias = link_alias ?? lastLinkAlias;
                            const url = `/menu/${alias}/waiting-order-approval/${restaurantId}/${sessionId}/${menuId}/${req?.id}`;
                            navigate(url);
                            setLoading(false);
                        }
                    } else {
                        setWaitForBill(true);
                        const res = await requestBill(restaurantId, sessionId, PAYMENT_TYPES.all);
                    }
                }
            }
        }catch(ex: any){
            const code = ex.code;
            console.log(code);
            switch (code){
                case RESPONSE_CODES.aborted:
                    setAskConfirm(true);
                    break;
                case RESPONSE_CODES.denied:
                    setIsRejected(true);
                    break;
                default:
                    _showErrorMessage(ex);
                    break;

            }
        }
        setLoading(false);
    }

    const _proceedWithOrder = () => {
        if (isRecap) {
            _proceedWithOrderFromRecap();
        } else {
            _proceedWithConfirmOrder(false);
        }
    };

    const totalPrice = sessionMenuItems.map((item) => evaluateDitsMenuItemPriceV2(item)).reduce((acc: number, price: number) => {
        return acc += (price * 1.0)
    }, 0) ?? 0;
   
    const _canProceedWithPriceOrder = () => {
        if(restaurant){
            _minimumOrderAmount = (restaurant.minimum_external_order_amount ?? 0) * 1.0;
            _isMajorThanMinimumPrice = totalPrice >= _minimumOrderAmount; 
            return (totalPrice > -1 && _isMajorThanMinimumPrice);
        }
        return false;
    }

    const _canProceedWithOrder = () => {
        const proceed = _canProceedWithPriceOrder() ? (isRecap ? ((hasTimeSlotSelection ? !selectedSlot : false)) : false) : true;
        return proceed;
    }

    if(loading) return (
        <LoaderGif  
            image={paymentMethod === 'cash' ? "/assets/loading_before_payment_cash.gif": "/assets/loading_before_payment_card.gif"}
            message={LocalizerHelper.localized((paymentMethod === 'cash') ? 'loading_msg_before_payment_cash' : 'loading_msg_before_payment_card')} 
        />);

    return <Screen className="screen order_recap_screen">
        <RestaurantHeader className="block"/>
        {hasTimeSlotSelection && <TimeSlots selectTimeSlot={_updateSessionTimeSlot}/>}
        <PaymentMethodButtons setPaymentMethod={_updateSessionPaymentMethod}/>
        {!isRecap && <DeliveryDetailsView/>}
        <div className="container">
            <div className="row">
                <div className="col-md-12 text-left">
                    <Typography variant="h4" gutterBottom align="left" className="recap-header">
                        {LocalizerHelper.localized('your_order_recap_header')}
                    </Typography>
                </div>
            </div>
        </div>
        <SessionMenuItems canDelete={true}/>
        <DeliveryCostsRow totalPrice={totalPrice}/>

        <BottomSheetOrderRecap 
            nextLabel={LocalizerHelper.localized('proceed_with_order') ?? ""}
            goToNext={_proceedWithOrder} 
            disableAction={ _canProceedWithOrder()}
            addDeliveryCosts = {true}
            disableLabel={_isMajorThanMinimumPrice ? null : `${LocalizerHelper.localized('minimum_order_amount')} ${formatPrice(_minimumOrderAmount)}`}
        />
        <ConfirmDialog open={askConfirm} body={LocalizerHelper.localized('confirm_message_order') ?? ""} handleClose={_closeConfirmDialog} handleConfirm={_confirmDialog}/>
        <ErrorDialog open={isInError} error={error} handleClose={_closeErrorDialog}/>
        <ErrorDialog open={isRejected} error={LocalizerHelper.localized('order_rejected_or_completed')} handleClose={_closeErrorAndGoHomepage}/>
    </Screen>
}     

export default OrderRecap;
